import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
  CartesianGrid,
} from 'recharts';
import {
  CheckCircleRounded as CheckCircleIcon,
  HourglassEmptyRounded as HourglassEmptyIcon,
  PeopleRounded as PeopleIcon,
  SpeedRounded as SpeedIcon,
  InsightsRounded as InsightsIcon,
  ArrowForwardRounded as ArrowForwardIcon,
  BeachAccessRounded as BeachIcon,
  HealthAndSafetyRounded as InsuranceIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';

import api from '../../services/api';
import AnnouncementAlertBanner from '../../components/Dashboard/AnnouncementAlertBanner';
import QuickActionMenu from '../../components/Dashboard/QuickActionMenu';
import { formatRoleLabel } from '../../utils/roleCapabilities';
import { daysUntilDate, formatDateOnly, isHolidayUpcoming } from '../../utils/notificationUtils';
import { useMotionPresets } from '../../theme/motion';
import { EmptyState, LoadingState, PageLayout } from '../../components/ui';
import { ManagerKpiTile } from '../../components/manager/ManagerVisuals';
import { AdminCommandBar, InboxReviewCard, ageLabel } from '../../components/admin/AdminVisuals';

const safeArray = (m) => (Array.isArray(m) ? m : []);
const numberOr = (n, fallback = 0) => (Number.isFinite(Number(n)) ? Number(n) : fallback);

const longDate = () =>
  new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });

// Mirror of ApprovalInboxController::stepBucket so optimistic updates move the
// right pipeline node when an item is resolved inline.
function stepBucket(step) {
  const key = String(step || '').trim().toLowerCase();
  if (key === 'manager') return 'manager';
  if (['finance', 'finance_coordinator', 'ceo'].includes(key)) return 'finance';
  return 'hr';
}

function AdminPanel({ title, subtitle, action, children }) {
  const theme = useTheme();
  const motionPresets = useMotionPresets();
  return (
    <Paper
      component={motion.div}
      variant="outlined"
      {...motionPresets.revealOnScroll}
      sx={{
        borderRadius: '12px',
        borderColor: theme.hr?.line?.soft || 'divider',
        backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.78) : alpha(theme.palette.background.paper, 0.96),
        boxShadow: 'none',
        p: { xs: 1.3, sm: 1.5 },
      }}
    >
      {title || action ? (
        <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between" sx={{ mb: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            {title ? (
              <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.15 }}>
                {title}
              </Typography>
            ) : null}
            {subtitle ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3 }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
          {action}
        </Stack>
      ) : null}
      {children}
    </Paper>
  );
}

export default function AdminDashboard() {
  const theme = useTheme();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [inbox, setInbox] = useState({ summary: {}, items: [] });
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState('');
  const [actingId, setActingId] = useState(null);
  const [declineItem, setDeclineItem] = useState(null);
  const [declineReason, setDeclineReason] = useState('');

  async function load() {
    setFetching(true);
    setError('');
    try {
      const [dashboardRes, inboxRes] = await Promise.all([
        api.get('/admin/dashboard'),
        api.get('/admin/approval-inbox', { params: { tab: 'all_pending', per_page: 50 } }).catch(() => ({ data: { summary: {}, items: [] } })),
      ]);
      setData(dashboardRes.data || {});
      setInbox({ summary: inboxRes.data?.summary || {}, items: safeArray(inboxRes.data?.items) });
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || 'Failed to load admin dashboard.');
      setData(null);
    } finally {
      setFetching(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function act(item, action, reason) {
    if (!item) return;
    const id = `${item.kind}:${item.id}`;
    if (actingId) return;
    setActingId(id);
    try {
      await api.post('/admin/approval-inbox/actions', {
        action,
        kind: item.kind,
        id: item.id,
        workflow_task_id: item.workflow_task_id || undefined,
        rejection_reason: action === 'reject' && reason && reason.trim() ? reason.trim() : undefined,
      });
      toast.success(`Request ${action === 'approve' ? 'approved' : 'declined'}.`);
      setInbox((prev) => {
        const items = safeArray(prev.items).filter((row) => !(row.kind === item.kind && row.id === item.id));
        const summary = { ...prev.summary, all_pending: Math.max(0, numberOr(prev.summary?.all_pending) - 1) };
        const bucket = stepBucket(item.current_step);
        summary.by_step = { ...(prev.summary?.by_step || {}), [bucket]: Math.max(0, numberOr(prev.summary?.by_step?.[bucket]) - 1) };
        return { ...prev, items, summary };
      });
      if (typeof window.refreshAssignedPendingCount === 'function') window.refreshAssignedPendingCount();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || 'Could not update request.');
    } finally {
      setActingId(null);
    }
  }

  const reportKpis = data?.requests_report?.kpis || {};
  const kpis = data?.kpis || {};
  const summary = inbox.summary || {};

  const pending = numberOr(summary.all_pending);
  const oldestLabel = summary.oldest_age_hours ? ageLabel(summary.oldest_age_hours) : null;
  const stages = summary.by_step || { manager: 0, hr: 0, finance: 0 };
  const headcount = numberOr(kpis.headcount_active);
  const completionRate = numberOr(reportKpis.completion_rate);
  const avgCycle = numberOr(reportKpis.avg_cycle_hours);
  const adminName = data?.admin?.name || '';
  const adminRole = formatRoleLabel(data?.admin?.role || 'hr_admin');

  const trend = useMemo(() => safeArray(data?.requests_report?.series?.requests_over_time), [data]);
  const upcomingHolidays = useMemo(
    () =>
      safeArray(data?.upcoming_holidays)
        .filter((h) => isHolidayUpcoming(h))
        .slice()
        .sort((a, b) => new Date(a.holiday_date) - new Date(b.holiday_date))
        .slice(0, 4),
    [data]
  );
  const announcements = safeArray(data?.announcements);
  const queue = safeArray(inbox.items).slice(0, 6);
  const insExpiring = numberOr(data?.insurance?.expiring_30d);
  const insExpired = numberOr(data?.insurance?.expired);

  const completionTone = completionRate >= 80 ? 'success' : completionRate >= 50 ? 'warning' : 'error';

  const kpiTiles = [
    { tone: 'warning', icon: <HourglassEmptyIcon fontSize="small" />, label: 'Pending approvals', value: pending, hint: oldestLabel ? `Oldest ${oldestLabel}` : 'All caught up', onClick: () => navigate('/all-requests?view=assigned_requests') },
    { tone: completionTone, icon: <CheckCircleIcon fontSize="small" />, label: 'Completion rate', value: `${completionRate}%`, hint: 'Closed on time' },
    { tone: 'info', icon: <SpeedIcon fontSize="small" />, label: 'Avg cycle time', value: `${avgCycle.toFixed(1)}h`, hint: 'Submit to decision' },
    { tone: 'primary', icon: <PeopleIcon fontSize="small" />, label: 'Headcount', value: headcount, hint: 'Active employees' },
  ];

  if (fetching) {
    return (
      <PageLayout backgroundAccent compact>
        <LoadingState rows={8} />
      </PageLayout>
    );
  }

  return (
    <PageLayout backgroundAccent compact>
      <Stack spacing={1.4}>
        <AdminCommandBar
          adminName={adminName}
          role={adminRole}
          dateLabel={longDate()}
          headcount={headcount}
          pending={pending}
          oldestLabel={oldestLabel}
          stages={stages}
          onRefresh={load}
          onReview={() => navigate('/all-requests?view=assigned_requests')}
          onStageClick={() => navigate('/all-requests?view=assigned_requests')}
          utility={
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                size="small"
                startIcon={<BeachIcon fontSize="small" />}
                onClick={() => navigate('/admin/my-leave')}
                sx={{ color: '#fff', backgroundColor: alpha('#ffffff', 0.14), '&:hover': { backgroundColor: alpha('#ffffff', 0.24) }, display: { xs: 'none', sm: 'inline-flex' } }}
              >
                My leave
              </Button>
              <Box sx={{ '& .MuiIconButton-root, & .MuiButton-root': { color: '#fff' } }}>
                <QuickActionMenu />
              </Box>
            </Stack>
          }
        />

        {error ? (
          <Alert severity="error" sx={{ borderRadius: '12px' }} onClose={() => setError('')}>
            {error}
          </Alert>
        ) : null}

        <AnnouncementAlertBanner announcements={announcements} />

        {insExpiring > 0 || insExpired > 0 ? (
          <Box
            component="button"
            type="button"
            onClick={() => navigate(`/admin/insurance-policies?status=${insExpiring > 0 ? 'expiring' : 'expired'}`)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              width: '100%',
              textAlign: 'left',
              font: 'inherit',
              cursor: 'pointer',
              p: '0.6rem 0.9rem',
              borderRadius: '12px',
              border: '1px solid',
              borderColor: alpha(theme.palette.warning.main, 0.3),
              backgroundColor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.16 : 0.08),
              color: theme.palette.warning.dark,
            }}
          >
            <InsuranceIcon sx={{ fontSize: 18 }} />
            <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
              {insExpiring > 0 ? `${insExpiring} insurance ${insExpiring === 1 ? 'policy' : 'policies'} expiring within 30 days` : ''}
              {insExpiring > 0 && insExpired > 0 ? ' · ' : ''}
              {insExpired > 0 ? `${insExpired} expired` : ''}
            </Typography>
            <ArrowForwardIcon fontSize="small" />
          </Box>
        ) : null}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }, gap: 1 }}>
          {kpiTiles.map((tile) => (
            <ManagerKpiTile key={tile.label} {...tile} />
          ))}
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1.55fr) minmax(320px, 0.55fr)' }, gap: 1.4, alignItems: 'start' }}>
          <AdminPanel
            title="Needs your decision"
            subtitle="Approve or decline inline. Oldest first, across every request type."
            action={
              <Button size="small" onClick={() => navigate('/all-requests?view=assigned_requests')}>
                View all
              </Button>
            }
          >
            {queue.length ? (
              <Stack spacing={0.9}>
                {queue.map((item) => (
                  <InboxReviewCard
                    key={`${item.kind}:${item.id}`}
                    item={item}
                    busy={actingId === `${item.kind}:${item.id}`}
                    onApprove={(row) => act(row, 'approve')}
                    onReject={(row) => {
                      setDeclineReason('');
                      setDeclineItem(row);
                    }}
                    onView={(row) =>
                      row.kind === 'hr_request'
                        ? navigate(`/all-requests/${row.id}`)
                        : navigate('/all-requests?view=assigned_requests')
                    }
                  />
                ))}
              </Stack>
            ) : (
              <EmptyState compact title="Approval queue is clear" description="Nothing is waiting on you right now." />
            )}
          </AdminPanel>

          <Stack spacing={1.4} sx={{ minWidth: 0 }}>
            <AdminPanel title="Request volume" subtitle="Rolling 12 months, all modules.">
              {trend.length ? (
                <Box sx={{ height: 190, width: '100%' }}>
                  <ResponsiveContainer>
                    <AreaChart data={trend} margin={{ top: 6, right: 10, bottom: 0, left: -18 }}>
                      <defs>
                        <linearGradient id="adminTrend" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={theme.palette.primary.main} stopOpacity={0.32} />
                          <stop offset="100%" stopColor={theme.palette.primary.main} stopOpacity={0.03} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 6" stroke={alpha(theme.palette.text.secondary, 0.14)} vertical={false} />
                      <XAxis dataKey="ym" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={28} />
                      <RTooltip />
                      <Area type="monotone" dataKey="count" stroke={theme.palette.primary.main} strokeWidth={2.5} fill="url(#adminTrend)" dot={false} activeDot={{ r: 5 }} />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <EmptyState compact title="No request volume yet" description="Nothing recorded in the rolling window." />
              )}
            </AdminPanel>

            <AdminPanel title="Upcoming holidays">
              {upcomingHolidays.length ? (
                <Stack spacing={0.8}>
                  {upcomingHolidays.map((h) => {
                    const d = daysUntilDate(h.holiday_date);
                    const label = d == null ? '-' : d === 0 ? 'Today' : d === 1 ? 'Tomorrow' : `${d} d`;
                    return (
                      <Stack key={h.id || h.holiday_date || h.name} direction="row" justifyContent="space-between" alignItems="center">
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                            {h.name || 'Holiday'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {formatDateOnly(h.holiday_date) || '-'}
                          </Typography>
                        </Box>
                        <Box component="span" sx={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', px: 1, py: 0.4, borderRadius: '8px', backgroundColor: alpha(theme.palette.primary.main, 0.08), color: 'text.secondary', whiteSpace: 'nowrap' }}>
                          {label}
                        </Box>
                      </Stack>
                    );
                  })}
                </Stack>
              ) : (
                <EmptyState compact title="No upcoming holidays" description="Nothing scheduled." />
              )}
            </AdminPanel>

            <AdminPanel title="Announcements">
              {announcements.length ? (
                <Stack spacing={0.9}>
                  {announcements.slice(0, 3).map((a) => (
                    <Box key={a.id || a.title}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {a.title || 'Announcement'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {formatDateOnly(a.start_date || a.created_at) || 'Active'}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <EmptyState compact title="No announcements" description="No active messages from HR." />
              )}
            </AdminPanel>
          </Stack>
        </Box>

        <Box
          component="button"
          type="button"
          onClick={() => navigate('/admin/analytics')}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.2,
            width: '100%',
            textAlign: 'left',
            font: 'inherit',
            cursor: 'pointer',
            p: 1.1,
            borderRadius: '12px',
            border: '1px solid',
            borderColor: theme.hr?.line?.soft || 'divider',
            backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.6 : 0.92),
            '&:hover': { borderColor: alpha(theme.palette.primary.main, 0.4) },
          }}
        >
          <Stack direction="row" spacing={1.2} alignItems="center" sx={{ minWidth: 0 }}>
            <InsightsIcon sx={{ color: 'primary.main' }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Workforce &amp; talent analytics
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Leave share, department leave days, headcount, exit/entry, hiring funnel, request mix
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'primary.main', flexShrink: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              View full report
            </Typography>
            <ArrowForwardIcon fontSize="small" />
          </Stack>
        </Box>

        <Dialog
          open={Boolean(declineItem)}
          onClose={() => (actingId ? null : setDeclineItem(null))}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: '12px' } }}
        >
          <DialogTitle sx={{ fontWeight: 700 }}>Decline request</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              Decline {declineItem?.employee?.name ? `${declineItem.employee.name}'s` : 'this'}{' '}
              {(declineItem?.type_label || 'request').toLowerCase()}? Add an optional reason the employee will see.
            </Typography>
            <TextField
              label="Reason (optional)"
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              autoFocus
              inputProps={{ maxLength: 2000 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeclineItem(null)} disabled={Boolean(actingId)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              disabled={Boolean(actingId)}
              onClick={async () => {
                const item = declineItem;
                await act(item, 'reject', declineReason);
                setDeclineItem(null);
              }}
            >
              {actingId ? 'Declining…' : 'Decline'}
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </PageLayout>
  );
}
